'use client';

import { cn } from '../utils/cn';
import type {
  TreeSelectionOption,
  TreeSelectionValue,
} from '../types/TreeOptionType';

type ReadOnlySelectTreeOptionsProps = {
  options: TreeSelectionOption[];
  value?: TreeSelectionValue;
  className?: string;
  name?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  // Translation function for validation messages
  t?: (
    key: string,
    options?: { count?: number; plural?: string; plural_verb?: string }
  ) => string;
} & React.HTMLAttributes<HTMLDivElement>;

// SVG Components for read-only display
const ReadOnlyCircleIcon = ({ isSelected }: { isSelected: boolean }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="block"
  >
    <rect
      x="0.5"
      y="0.5"
      width="15"
      height="15"
      rx="7.5"
      stroke="#D9D9D9"
      strokeWidth="1"
    />
    <circle cx="8" cy="8" r="6" fill={isSelected ? '#34BBEB' : ''} />
  </svg>
);

export default function ReadOnlySelectTreeOptions({
  options,
  value = { key: '', textValue: '' },
  className = '',
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedby,
  t,
  ...rest
}: ReadOnlySelectTreeOptionsProps) {
  return (
    <div className="flex flex-col gap-2.5">
      {options.map((option) => {
        const optionKey = option.key;
        const isOptionSelected = value.key === optionKey;
        const optionTextValue = isOptionSelected ? value.textValue || '' : '';

        return (
          <div key={option.key}>
            <div className="flex flex-col gap-2.5">
              <div className="grid grid-cols-[20px_1fr] items-start gap-2 min-w-0">
                {/* Read-only SVG radio button */}
                <div className="mr-2 flex-shrink-0">
                  <ReadOnlyCircleIcon isSelected={isOptionSelected} />
                </div>

                <span
                  className={cn(
                    'text-14 font-medium',
                    isOptionSelected ? 'text-el-grey-900' : 'text-el-grey-500'
                  )}
                >
                  {option.label}
                </span>
              </div>

              {/* Text field for option if it has one and is selected */}
              {isOptionSelected && option.hasTextField && optionTextValue && (
                <div className="ml-6">
                  <div className="text-12 text-el-grey-700 bg-el-grey-50 px-3 py-2 rounded border border-el-grey-300">
                    {optionTextValue}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
